import React from "react";

import { useModal } from "@editor/hooks/useModal";

import IconButton from "@replo/design-system/components/button/IconButton";
import { Combobox } from "@replo/design-system/components/combobox/Combobox";
import { MenuItem } from "@replo/design-system/components/menu/MenuItem";
import twMerge from "@replo/design-system/utils/twMerge";
import { EyeOff, Paintbrush, Settings, Settings2, Trash2 } from "lucide-react";
import { animated, config, useSpring } from "react-spring";

import { useEditorStoreActions, usePanelView } from "../../store/editorStore";
import { useCurrentFile } from "../../utils/useCurrentFile";

const PageSettingsMenu = () => {
  const [isSettingsComboboxOpen, setIsSettingsComboboxOpen] =
    React.useState(false);
  const panelView = usePanelView();
  const { setPanelView } = useEditorStoreActions();
  const { openModal } = useModal();
  const currentFile = useCurrentFile();

  const isPagePublished = Boolean(currentFile?.lastPublishedFileVersionId);

  // Standard menu animation
  const transitions = useSpring({
    opacity: isSettingsComboboxOpen ? 1 : 0,
    transform: isSettingsComboboxOpen ? `scale(${1})` : `scale(${0.9})`,
    config: config.stiff,
  });

  return (
    <Combobox.Root
      open={isSettingsComboboxOpen}
      onOpenChange={setIsSettingsComboboxOpen}
      onChange={(value: string) => {
        if (value === "design-library") {
          setPanelView("designLibrary");
        } else if (value === "page-settings") {
          setPanelView("pageSettings");
        } else if (value === "unpublish-page") {
          if (currentFile) {
            openModal({
              type: "unpublishModal",
              props: {
                type: "page",
                page: currentFile,
              },
            });
          }
        } else if (value === "delete-page") {
          // eslint-disable-next-line unicorn/no-lonely-if -- it's more intuitive this way
          if (currentFile) {
            openModal({
              type: "deletePageModal",
              props: {
                page: currentFile,
              },
            });
          }
        }
        setIsSettingsComboboxOpen(false);
      }}
      options={[
        {
          value: "page-settings",
          label: (
            <MenuItem
              variant="default"
              selected={panelView === "pageSettings"}
              startEnhancer={<Settings size={16} />}
            >
              Page Settings
            </MenuItem>
          ),
          displayValue: "Page Settings",
        },
        {
          value: "design-library",
          label: (
            <MenuItem
              variant="default"
              selected={panelView === "designLibrary"}
              startEnhancer={<Paintbrush size={16} />}
            >
              Design Library
            </MenuItem>
          ),
          displayValue: "Design Library",
        },
        {
          value: "unpublish-page",
          label: (
            <MenuItem
              variant="default"
              startEnhancer={<EyeOff size={16} />}
              disabled={!isPagePublished}
              toolTipText={
                !isPagePublished
                  ? "Page must be published to unpublish"
                  : undefined
              }
            >
              Unpublish Page
            </MenuItem>
          ),
          displayValue: "Unpublish Page",
        },
        {
          value: "delete-page",
          label: (
            <MenuItem
              variant="default"
              startEnhancer={<Trash2 className="text-danger" size={16} />}
              UNSAFE_className="text-danger"
              disabled={isPagePublished}
              toolTipText={
                isPagePublished ? "Unpublish this page to delete" : undefined
              }
            >
              Delete Page
            </MenuItem>
          ),
          displayValue: "Delete Page",
        },
      ]}
      layoutClassName="ml-auto"
    >
      <Combobox.Trigger>
        <IconButton
          variant="secondary"
          size="base"
          UNSAFE_className={twMerge(
            (panelView === "designLibrary" || panelView === "pageSettings") &&
              "bg-primary/10 border border-primary hover:bg-primary/10",
          )}
          icon={
            <Settings2
              className={twMerge(
                "text-muted",
                (panelView === "designLibrary" ||
                  panelView === "pageSettings") &&
                  "text-primary",
              )}
              size={16}
            />
          }
        />
      </Combobox.Trigger>
      <Combobox.Popover layoutClassName="w-48">
        <animated.div style={transitions}>
          <Combobox.Content />
        </animated.div>
      </Combobox.Popover>
    </Combobox.Root>
  );
};

export default PageSettingsMenu;
